import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PrefKey,
  GridPreferences,
  getCustomViewSubject,
  getUser,
  getUserPreferencesSubject,
  isCapabilityDenied,
  isInternalUser,
  isRelatedInventoryReportsEnabled,
  UserPreferencesContent
} from '../../../store/User';
import { CustomFieldCapability } from '../../../utils/customFieldCapabilities';
import {
  configAccountIdFor,
  EMPTY_GRID,
  useAccountGridConfig,
  useAccountGridReady
} from '../../../store/AccountCustomization';
import { TabPanel } from '../../Atoms/TabPanel';
import { PageCard } from '../../Compounds/CardWithTitle';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import {
  getInventoryByProjectId,
  getInventoryLoadingSubject,
  getInventorySubject,
  setInventoryCustomFields,
  useInventory
} from '../../../store/Inventory';
import { CustomFieldDefinition, InventoryAsset } from '../../../types';
import { GridSingleSelectColDef } from '@mui/x-data-grid';
import {
  fetchProjectById,
  fetchProjects,
  rereadProjectIfReading,
  setRelatedInventoryCustomFields
} from '../../../store/Project';
import { normalizeRiskItems } from '../../../store/ProjectsRelatedInventory';
import { RelatedInventoryRiskItem } from '../../../Api/Projects/projectsApi';
import { getGridColumns } from '../../Compounds/UpstackDataGrid/helpers';
import {
  accountVisibilityChanges,
  withAccountDefaultLayout
} from '../../Compounds/UpstackDataGrid/accountDefaultLayout';
import { TabProps } from '../Inventory/InventoryPage';
import { createCustomFieldColumns, projectsRelatedInventoryColumns } from './ProjectsRelatedInventoryGridColumnDefs';
import {
  customFieldCellEditable,
  makeCustomFieldRowUpdate,
  savedDateMovesRisk
} from '../../Compounds/UpstackDataGrid/customFieldColumns';
import { foreignRowAccountIds } from '../../Compounds/UpstackDataGrid/foreignRowAccountIds';
import { useCustomFieldAcceptance } from '../../../Api/Admin/CustomFields/useCustomFieldAcceptance';
import { inventoryRowAccountId } from '../Inventory/inventoryGridColumnDefs';
import { fetchCustomFieldDefinitionsApi } from '../../../Api/Admin/CustomFields/customFieldDefinitionsApi';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { DetailsDrawer } from '../../Compounds/DetailsDrawer';
import { getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { GridTabsHeader } from '../../Compounds/GridTabsHeader';
import { ViewsModal } from '../../Compounds/CustomTableViews/ViewsModal';
import { NewViewModal } from '../../Compounds/CustomTableViews/NewViewModal';
import { parseProjectScopedViewName, buildProjectScopedName } from '../../Compounds/CustomTableViews/viewKey';
import { useFeatureFlags } from '../../../Api/useFeatureFlags';
import { SavedViewControls } from '../../Compounds/CustomTableViews/SavedViewControls';

export interface ProjectsRelatedInventoryProps {
  id: string;
  // Scored rows from the project detail payload; when absent (flag off or the detail
  // fetch failed) the table falls back to the inventory store, without a Risk column.
  relatedInventory?: RelatedInventoryRiskItem[];
  // The project's owning account; custom field definitions belong to it. Passed
  // explicitly because internal staff view other accounts' projects, where the
  // logged-in user's account and the global selector don't identify it.
  accountId?: string;
}

// One empty list for every render, or the columns memoized on it, and the grid with them, rebuild each time.
const EMPTY_DISABLED_FIELDS: string[] = [];

export const ProjectsRelatedInventory = ({ id, relatedInventory, accountId }: ProjectsRelatedInventoryProps) => {
  const page = 'Related Inventory';
  const hasRiskData = relatedInventory !== undefined;
  // Risk rows come from the project payload, so only a project re-read shows a tag written on one.
  const refetchProject = useCallback(async () => {
    // This table's rows are the project's related inventory, so the re-read has to ask for it back.
    await fetchProjectById(id, true);
  }, [id]);
  const tagRefetch = hasRiskData ? refetchProject : undefined;
  const [inventory, setInventory] = useState<RelatedInventoryRiskItem[]>(
    relatedInventory ? normalizeRiskItems(relatedInventory) : []
  );
  const [inventoryLoading, setInventoryLoading] = useState<boolean>(false);
  const [tabs, setTabs] = useState<TabProps[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [userPreferences, setUserPreferences] = useState<UserPreferencesContent>();
  const [drawerAsset, setDrawerAsset] = useState<RelatedInventoryRiskItem | null>(null);
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDefinition[]>([]);
  const customFieldDefsRef = useRef<CustomFieldDefinition[]>([]);
  const mounted = useRef(true);

  const { fetchInventory } = useInventory();
  const { setSnack } = useSnackbar();
  const { config: featureFlags } = useFeatureFlags();
  const relatedInventoryReportsEnabled = isRelatedInventoryReportsEnabled(featureFlags);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [triggerSave, setTriggerSave] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [customTabNames, setCustomTabNames] = useState<string[]>([]);
  const [selectedCustomView, setSelectedCustomView] = useState<string>('');
  const [search] = useSearchParams();
  const detailId = search.get('detailId');

  const customFieldAccountId = configAccountIdFor(
    accountId,
    inventory.find((item) => inventoryRowAccountId(item))
  );
  const accountGridConfig = useAccountGridConfig(customFieldAccountId, 'related_project_assets_grid');
  const accountConfigReady = useAccountGridReady(customFieldAccountId);

  const otherRowAccountIds = useMemo(
    () => foreignRowAccountIds(inventory, inventoryRowAccountId, customFieldAccountId ?? null),
    [inventory, customFieldAccountId]
  );
  const acceptance = useCustomFieldAcceptance(otherRowAccountIds, 'ProjectDetail');

  // Rebuilt each render rather than memoized so an account switch can never leave the gate on the previous account's set.
  const customFieldCellIsEditable = customFieldCellEditable({
    definitionsAccountId: customFieldAccountId ?? null,
    rowAccountId: inventoryRowAccountId,
    acceptance,
    unrestricted: isInternalUser(getUser()),
    denied: isCapabilityDenied(CustomFieldCapability.VALUES_UPSERT, getUser()),
    definitions: customFieldDefs
  });

  // The account-allowed set of columns for this grid, enforced on every render so an auto-saved personal
  // view can't re-show hidden ones. Staff read it live; customers get the delivered base key.
  const accountDisabledFields = useMemo(
    () => accountGridConfig?.disabledFields ?? EMPTY_DISABLED_FIELDS,
    [accountGridConfig]
  );
  const accountLayout = accountGridConfig?.muiConfig?.columns;

  // Derived in render, so the rows, the custom fields and the account's layout reach the grid together.
  const columns: GridSingleSelectColDef[] = useMemo(
    () =>
      withAccountDefaultLayout(
        [
          ...getGridColumns(
            inventory,
            projectsRelatedInventoryColumns(hasRiskData, tagRefetch),
            'related_project_assets_grid',
            accountDisabledFields
          ),
          ...createCustomFieldColumns<RelatedInventoryRiskItem>(customFieldDefs, accountDisabledFields)
        ],
        accountLayout
      ),
    [inventory, hasRiskData, tagRefetch, customFieldDefs, accountDisabledFields, accountLayout]
  );

  // Base tabs (just "All") show as tabs; saved views are picked from the My Views dropdown, whose
  // selection maps to a tab index sitting after the base tabs.
  const handleTabChange = (index: number, name?: string, customTab?: boolean) => {
    if (customTab && name) {
      setSelectedCustomView(name);
    } else {
      setSelectedCustomView('');
      setSelectedTab(index);
    }
  };

  // Mirrors the account page: the Save As View button flips triggerSave, the grid persists the current
  // layout and calls back here to reset the flag and open the naming modal.
  const handleNewTabModalOpen = () => {
    setTriggerSave(false);
    setCreateOpen(true);
  };

  // Risk rows come from the project detail payload, so the drawer's Refresh must
  // refetch the project (the legacy default, fetchInventory, feeds nothing here).
  const refreshRiskRows = async () => {
    setInventoryLoading(true);
    await refetchProject();
    // Guard against the fetch resolving after the page navigated away.
    if (mounted.current) setInventoryLoading(false);
  };

  useEffect(() => {
    mounted.current = true;
    const userPreferencesSubject = getUserPreferencesSubject().subscribe((prefs) => {
      setUserPreferences(prefs.content);
    });

    fetchProjects();

    return () => {
      mounted.current = false;
      if (userPreferencesSubject) userPreferencesSubject.unsubscribe();
    };
  }, []);

  // This project's own saved related-inventory views become its custom tabs. Views for other projects
  // and account-wide ones are filtered out by the UUID the name is prefixed with.
  useEffect(() => {
    const sub = getCustomViewSubject().subscribe((views) => {
      setCustomTabNames(
        views
          .filter((view) => view.page.startsWith('related_project_assets'))
          .map((view) => parseProjectScopedViewName(view.name))
          .filter((parsed): parsed is { projectId: string; displayName: string } => parsed?.projectId === id)
          .map((parsed) => parsed.displayName)
      );
    });
    return () => sub.unsubscribe();
  }, [id]);

  // A deleted view must not leave the grid stuck on a tab that no longer exists.
  useEffect(() => {
    if (selectedCustomView && !customTabNames.includes(selectedCustomView)) {
      setSelectedCustomView('');
      setSelectedTab(0);
    }
  }, [customTabNames, selectedCustomView]);

  useEffect(() => {
    if (!customFieldAccountId) {
      customFieldDefsRef.current = [];
      setCustomFieldDefs([]);
      return;
    }
    fetchCustomFieldDefinitionsApi(customFieldAccountId, undefined, 'ProjectDetail').then(({ data }) => {
      // Clear on a failed/empty response so a prior account's columns don't linger after a switch.
      customFieldDefsRef.current = data ?? [];
      setCustomFieldDefs(data ?? []);
    });
  }, [customFieldAccountId]);

  useEffect(() => {
    if (!hasRiskData) return;

    const normalized = normalizeRiskItems(relatedInventory);
    setInventory(normalized);
    // A legacy->risk flip can strand an in-flight fetch's loading=true after unsubscribe.
    setInventoryLoading(false);
    setTabs([{ name: 'All', count: normalized.length, customView: false }]);
  }, [relatedInventory]);

  useEffect(() => {
    if (hasRiskData) return;

    const inventorySubject = getInventorySubject().subscribe((data) => {
      setInventory(getInventoryByProjectId(id));
      setTabs([{ name: 'All', count: data.length, customView: false }]);
    });
    const inventoryLoadingSubject = getInventoryLoadingSubject().subscribe((loading) => setInventoryLoading(loading));

    fetchInventory();

    return () => {
      inventorySubject.unsubscribe();
      inventoryLoadingSubject.unsubscribe();
    };
  }, [hasRiskData]);

  const handleCustomFieldRowUpdate = useMemo(
    () =>
      makeCustomFieldRowUpdate<RelatedInventoryRiskItem>({
        entityType: 'ProjectDetail',
        getDefinitions: () => customFieldDefsRef.current,
        persist: (asset, saved) => {
          setInventory((prev) => prev.map((item) => (item.id === asset.id ? asset : item)));

          // Persist to the store the grid re-reads on remount, or navigating back shows stale values until a
          // full refetch. Risk rows live on the project's relatedInventory; legacy rows in the inventory store.
          if (hasRiskData) {
            setRelatedInventoryCustomFields(id, asset.id as string, asset.custom_fields ?? []);
            if (savedDateMovesRisk(saved)) void refetchProject();
            else void rereadProjectIfReading(id, true);
          } else {
            setInventoryCustomFields(asset.id as string, asset.custom_fields ?? []);
          }
        }
      }),
    [id, hasRiskData, refetchProject]
  );

  const handleCustomFieldRowUpdateError = useCallback(
    (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to save custom field.';
      setSnack({ message, open: true, type: 'error' });
    },
    [setSnack]
  );

  useEffect(() => {
    if (!detailId) return;
    // Clearing on a miss drops stale drawer content when the open row leaves the
    // row set (a refresh reassigned it, or the mode flipped and the store lacks it).
    setDrawerAsset(inventory.find((ia) => ia?.id === detailId) ?? null);
  }, [detailId, inventory]);

  const customViewTabs: TabProps[] = relatedInventoryReportsEnabled
    ? customTabNames.map((name) => ({ name, count: 0, customView: true }))
    : [];
  const allTabs: TabProps[] = [...tabs, ...customViewTabs];

  // Derived, not stored: deleting a different view shifts the custom indices, so the active panel is
  // recomputed from the selected view's name each render rather than from a stale stored index. Gated on
  // the flag too — it delivers live, so a flip to off drops the custom panels and this must not point past
  // them into a blank grid.
  const customIndex =
    relatedInventoryReportsEnabled && selectedCustomView ? customTabNames.indexOf(selectedCustomView) : -1;
  const activeTab = customIndex >= 0 ? tabs.length + customIndex : selectedTab;

  // Custom tabs store their layout under the project-scoped key; the default All tab stays account-wide.
  const pageKeyForTab = (tab: TabProps): PrefKey =>
    (tab.customView
      ? `related_project_assets_grid_${buildProjectScopedName(id, tab.name)}`
      : `related_project_assets_grid_${tab.name}`) as PrefKey;

  const gridPropsFor = (tab: TabProps) =>
    accountVisibilityChanges(
      (userPreferences?.[pageKeyForTab(tab)] ?? EMPTY_GRID) as GridPreferences,
      accountGridConfig
    );

  const selectedTabRawName = allTabs[activeTab]?.customView
    ? buildProjectScopedName(id, allTabs[activeTab].name)
    : allTabs[activeTab]?.name || 'All';

  return (
    <>
      <PageCard
        childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
        title={page}>
        <>
          <GridTabsHeader
            labels={tabs.map((tab) => `${tab.name} (${inventory.length})`)}
            selectedTab={activeTab}
            onTabChange={(index) => handleTabChange(index, tabs?.[index]?.name)}>
            {relatedInventoryReportsEnabled && (
              <SavedViewControls
                selectedCustomView={selectedCustomView}
                customTabs={customTabNames}
                onSelectView={(name) => handleTabChange(0, name, true)}
                onSaveAsView={() => setTriggerSave(true)}
                saving={triggerSave}
                onScheduleReport={() => setScheduleOpen(true)}
              />
            )}
          </GridTabsHeader>
          {allTabs.map((tab, index) => (
            <TabPanel
              key={`${index}`}
              index={index}
              value={activeTab}>
              <UpstackDataGrid
                dataCy="projects-data-grid"
                columns={columns}
                {...gridPropsFor(tab)}
                loadingData={inventoryLoading || !accountConfigReady}
                page={pageKeyForTab(tab)}
                triggerSave={triggerSave}
                handleNewTabModalOpen={handleNewTabModalOpen}
                // Pinning Risk keeps it first even for users whose saved column order
                // predates the move (order reconciliation never re-pins saved fields).
                pinnedColumns={{ left: hasRiskData ? ['details', 'riskLabel'] : ['details'] }}
                isCellEditable={customFieldCellIsEditable}
                processRowUpdate={handleCustomFieldRowUpdate}
                onProcessRowUpdateError={handleCustomFieldRowUpdateError}
                rows={inventory}
                showSearch
                title={`Related Inventory - ${tab.name}`}
              />
            </TabPanel>
          ))}
        </>
      </PageCard>
      <div className="h-2"></div>
      {relatedInventoryReportsEnabled && (
        <>
          <ViewsModal
            open={scheduleOpen}
            handleClose={() => setScheduleOpen(false)}
            type="related_project_assets"
            projectId={id}
          />
          <NewViewModal
            open={createOpen}
            setOpen={setCreateOpen}
            sourceTab={selectedTabRawName}
            type="related_project_assets"
            projectId={id}
          />
        </>
      )}
      <DetailsDrawer
        // Risk rows carry the full asset payload (same parse as the inventory list).
        data={(drawerAsset ?? {}) as InventoryAsset}
        showDrawer={!!detailId}
        onRefresh={hasRiskData ? refreshRiskRows : undefined}
        {...getMetaData('project-asset')}
      />
    </>
  );
};
